/**
 * Порожній стан області (UX-37; states.md, розд. 1): пояснює причину і веде до наступної дії.
 * Анатомія — `StateView`: іконка 24 px, заголовок, одне речення, одна основна дія (вона перша в
 * порядку `Tab` області). Ілюстрацій немає.
 *
 * Чотири причини:
 * - `none` — записів ще немає: «Тут з'являться ваші …» + «Створити …», лише з правом створювати;
 *   без права — «Вони з'являться, коли їх призначать вам» (подання «мої») або «…створять колеги»;
 * - `filtered` — фільтри нічого не знайшли: перелік фільтрів + «Скинути фільтри»;
 * - `search` — пошук нічого не знайшов: «Нічого не знайдено за «…»» + дія викликача;
 * - `done` — усе зроблено: заголовок і наступна корисна дія від викликача.
 *
 * Слова — з метаданих сутності (форми `NounUk`, AG-04): платформа не знає самого слова. Без форм — нейтральне «записи».
 */
import type { NounUk } from '@clientline/contracts/i18n';
import { noun } from '@clientline/contracts/i18n';
import type { ReactNode } from 'react';
import { empty as t } from '../i18n/uk/empty.ts';
import type { IconName } from './Icon.tsx';
import { StateView } from './StateView.tsx';

export type EmptyReason = 'none' | 'filtered' | 'search' | 'done';

export interface EmptyAction {
  label: string;
  onAction: () => void;
}

export interface EmptyStateProps {
  reason: EmptyReason;
  /** Форми слова сутності з метаданих. */
  noun?: NounUk | undefined;
  /** Подання «мої»: «Тут з'являться ваші …». */
  own?: boolean;
  /** Чи може людина створювати записи (метадані `canCreate`). */
  canCreate?: boolean;
  /** `none`: дія створення; `search` / `done` — наступна корисна дія. */
  action?: EmptyAction | undefined;
  /** `filtered`: підписи діючих фільтрів і скидання. */
  filters?: readonly string[];
  onResetFilters?: () => void;
  /** `search`: запит людини. */
  query?: string;
  /** Свій заголовок і текст: напр. порожній день панелі «Сьогодні». */
  title?: string | undefined;
  text?: string | undefined;
  icon?: IconName;
}

const ICONS: Record<EmptyReason, IconName> = {
  none: 'inbox',
  filtered: 'search',
  search: 'search',
  done: 'check-circle',
};

/** Тексти порожнього стану — окремо від розмітки: їх бере й пошук у меню. */
export function emptyTexts(props: EmptyStateProps): { title: string; text?: string | undefined } {
  const plural = props.noun ? noun(props.noun, { plural: true }) : t.emptyRecords;
  switch (props.reason) {
    case 'none':
      return {
        title: props.title ?? (props.own ? t.emptyNoneOwn(plural) : t.emptyNone(plural)),
        text:
          props.text ??
          (props.canCreate ? undefined : props.own ? t.emptyAssigned : t.emptyByColleagues),
      };
    case 'filtered': {
      const genitive = props.noun
        ? noun(props.noun, { case: 'gen', plural: true })
        : t.emptyRecords;
      const filters = props.filters?.length
        ? `${t.emptyFilters}: ${props.filters.join(', ')}`
        : undefined;
      return { title: props.title ?? t.emptyFiltered(genitive), text: props.text ?? filters };
    }
    case 'search':
      return { title: props.title ?? t.emptySearch(props.query ?? ''), text: props.text };
    case 'done':
      return { title: props.title ?? t.emptyDone, text: props.text };
  }
}

export function EmptyState(props: EmptyStateProps) {
  const { title, text } = emptyTexts(props);
  const action: ReactNode =
    props.reason === 'filtered' && props.onResetFilters ? (
      <button className="btn btn--primary" type="button" onClick={props.onResetFilters}>
        {t.emptyResetFilters}
      </button>
    ) : props.action && (props.reason !== 'none' || props.canCreate) ? (
      <button className="btn btn--primary" type="button" onClick={props.action.onAction}>
        {props.action.label}
      </button>
    ) : null;
  return (
    <div data-testid="empty-state" data-reason={props.reason}>
      <StateView icon={props.icon ?? ICONS[props.reason]} title={title} text={text}>
        {action}
      </StateView>
    </div>
  );
}

/** Підпис дії створення з форм слова: «Створити демо-задачу». */
export { createLabel } from '../i18n/words.ts';
